// transformFields — the Location / Rotation / Scale rows of the inspector's
// Transform section, and what typing in one of them means.
//
// Modelled on Unreal's Details panel (FComponentTransformDetails): three
// labelled rows, each three per-axis numeric entries, and a preserve-ratio lock
// on Scale. Unity's Inspector Transform component is the same shape. Deriving
// the rows here rather than in JSX keeps the two things worth pinning testable
// without a browser: which axes are editable for a given object, and what a
// typed value does to the stored transform.
//
// Two conversions are deliberate and live only in this module:
//
//   ROTATION is stored in RADIANS (the adapters hand their values straight to
//   three's `rotation`) and shown in DEGREES. Nobody types 1.5708. The tune
//   panel still shows raw radians — it is the power-user surface — so this is
//   the only place the two representations meet.
//
//   SCALE for a 'uniform' object is one number wearing three hats. Typing into
//   any axis sets all three, which IS Unreal's preserve-ratio lock, forced on:
//   the adapter's write() collapses to a single factor anyway
//   (collapseUniformScale), so offering three independent fields would be a
//   promise the storage cannot keep.
//
// Capability gating follows the same rule as the menubar: an axis that cannot
// be edited is rendered DISABLED and says why — never hidden, never a field
// that silently discards what you type. Caps come from transformAdapters
// (translate 'xyz'|'xz'|false, rotate 'xyz'|'y'|false, scale 'xyz'|'uniform'|false).
//
// No imports — leaf module, driven directly by tests/unit/transformFields.test.mjs.

const RAD2DEG = 180 / Math.PI
const DEG2RAD = Math.PI / 180

/** Smallest scale we will write. A zero or negative scale collapses geometry
 *  irrecoverably, and no DCC lets you type one. Mirrors MIN_SCALE in
 *  transformAdapters. */
export const MIN_SCALE = 0.001

export const AXES = Object.freeze(['x', 'y', 'z'])

/**
 * The three rows, in Unreal's order. `key` indexes the adapter's TRS object;
 * `decimals` is display precision — degrees need less than metres.
 */
export const TRANSFORM_ROWS = Object.freeze([
  Object.freeze({ mode: 'translate', label: 'Location', key: 'position', unit: 'm', step: 0.01, decimals: 3 }),
  Object.freeze({ mode: 'rotate', label: 'Rotation', key: 'rotation', unit: '°', step: 1, decimals: 1 }),
  Object.freeze({ mode: 'scale', label: 'Scale', key: 'scale', unit: '×', step: 0.01, decimals: 3 }),
])

/** Which axes a cap string permits. Mirrors AXES_BY_CAP in transformAdapters —
 *  'uniform' permits all three because uniformity is enforced in the WRITE. */
const AXES_BY_CAP = {
  xyz: { x: true, y: true, z: true },
  xz: { x: true, y: false, z: true },
  y: { x: false, y: true, z: false },
  uniform: { x: true, y: true, z: true },
}

/** Stored value → what the field shows. */
export function toDisplayValue(mode, stored) {
  if (!Number.isFinite(stored)) return null
  return mode === 'rotate' ? stored * RAD2DEG : stored
}

/** What the user typed → what gets stored. */
export function fromDisplayValue(mode, typed) {
  if (!Number.isFinite(typed)) return null
  return mode === 'rotate' ? typed * DEG2RAD : typed
}

/**
 * Trim to `decimals` without trailing zeros: "0.5" not "0.500", "0" not
 * "0.000". A field full of noise digits is harder to read than one that says
 * the number.
 */
export function formatValue(value, decimals) {
  if (!Number.isFinite(value)) return ''
  const rounded = Number(value.toFixed(decimals))
  // -0 formats as "0": a negated zero is the same position, and "-0" reads as
  // a bug in the readout.
  return String(rounded === 0 ? 0 : rounded)
}

/**
 * Build the rows for one selection.
 *
 * @param {{position?:number[],rotation?:number[],scale?:number[]}|null} trs
 *   the adapter's read() result.
 * @param {{translate?:string|false,rotate?:string|false,scale?:string|false}} caps
 * @returns {Array<{mode:string,label:string,unit:string,step:number,
 *                  available:boolean,disabledReason:string|null,locked:boolean,
 *                  axes:Array<{axis:string,index:number,value:string,
 *                              enabled:boolean,reason:string|null}>}>}
 */
export function buildTransformFields(trs, caps = {}) {
  return TRANSFORM_ROWS.map(row => {
    const cap = caps?.[row.mode]
    const available = Boolean(cap) && Array.isArray(trs?.[row.key])
    const allowed = AXES_BY_CAP[cap] ?? null
    // 'uniform' is the preserve-ratio lock, forced on — see the header.
    const locked = cap === 'uniform'
    const disabledReason = !cap
      ? `${row.label.toLowerCase()} is not editable for this object`
      : !Array.isArray(trs?.[row.key])
        ? 'no value reported'
        : null

    return {
      ...row,
      available,
      disabledReason,
      locked,
      axes: AXES.map((axis, index) => {
        const axisAllowed = available && !!allowed?.[axis]
        const display = toDisplayValue(row.mode, trs?.[row.key]?.[index])
        return {
          axis,
          index,
          value: axisAllowed || available ? formatValue(display, row.decimals) : '',
          enabled: axisAllowed,
          reason: axisAllowed ? null
            : available ? `${axis.toUpperCase()} is not editable for this object`
              : disabledReason,
        }
      }),
    }
  })
}

/**
 * Apply one typed value, returning the FULL next TRS to hand adapter.write().
 *
 * Returns null when nothing should be written — an unparseable entry, a
 * disallowed axis, or a value identical to what is already stored. That last
 * one matters: a blur that re-commits the same number must not cost an undo
 * step or mark the object dirty.
 *
 * @returns {{position:number[],rotation:number[],scale:number[]}|null}
 */
export function applyAxisEdit({ trs, caps = {}, mode, axis, typed }) {
  const row = TRANSFORM_ROWS.find(r => r.mode === mode)
  if (!row || !trs) return null

  const cap = caps?.[mode]
  if (!cap) return null
  const allowed = AXES_BY_CAP[cap] ?? null
  if (!allowed?.[axis]) return null

  const current = trs[row.key]
  if (!Array.isArray(current)) return null

  const index = AXES.indexOf(axis)
  if (index < 0) return null

  const parsed = typeof typed === 'number' ? typed : Number.parseFloat(typed)
  let stored = fromDisplayValue(mode, parsed)
  if (stored == null) return null

  if (mode === 'scale') {
    // Never write a collapsed or mirrored scale — no DCC accepts one typed.
    if (stored < MIN_SCALE) stored = MIN_SCALE
  }

  const next = [...current]
  if (cap === 'uniform') {
    // One number, three hats: set every axis so what the fields show and what
    // the storage holds cannot disagree.
    next[0] = stored
    next[1] = stored
    next[2] = stored
  } else {
    next[index] = stored
  }

  const unchanged = next.every((v, i) => Object.is(v, current[i]))
  if (unchanged) return null

  return {
    position: [...(trs.position ?? [0, 0, 0])],
    rotation: [...(trs.rotation ?? [0, 0, 0])],
    scale: [...(trs.scale ?? [1, 1, 1])],
    [row.key]: next,
  }
}

/** Undo-coalescing key: consecutive keystrokes on ONE axis are one entry, and
 *  moving to another axis starts a new one (transformHistory.recordEdit). */
export function editKey(mode, axis) {
  return `field:${mode}.${axis}`
}
